import React from 'react';
import * as R from 'remeda';
import { useIntl } from 'react-intl';
import { IconCustomMenuDown } from '@rossum/ui/icons/tabler';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  SvgIcon,
  Typography,
} from '@rossum/ui/material';
import { useFieldManagerDataContext } from '../../hooks/FieldManagerDataContext';
import { Queue } from '../../types/queue';
import { Dialog } from '../ui/Dialog';
import { LinkWithNameAndId } from '../ui/LinkWithNameAndId';

export type DataForQueuesDialog = {
  fieldId: string;
  queues: Queue[];
};

type QueueDialogProps = {
  rowForQueuesDialog: DataForQueuesDialog;
  setRowForQueuesDialog: React.Dispatch<
    React.SetStateAction<DataForQueuesDialog | null>
  >;
};

export const QueuesDialog = ({
  rowForQueuesDialog,
  setRowForQueuesDialog,
}: QueueDialogProps) => {
  const { workspaces } = useFieldManagerDataContext();
  const intl = useIntl();
  const sortedNonEmptyWorkspaces = R.sortBy(
    workspaces,
    workspace => workspace.name
  ).filter(workspace =>
    rowForQueuesDialog.queues.some(queue => queue.workspace === workspace.url)
  );
  const queuesByWorkspace = R.groupBy(
    rowForQueuesDialog.queues,
    queue => queue.workspace ?? ''
  );

  return (
    <Dialog
      onClose={() => setRowForQueuesDialog(null)}
      open
      title={intl.formatMessage(
        {
          id: 'features.fieldManager.overview.queuesDialog.title',
        },
        { fieldId: rowForQueuesDialog.fieldId }
      )}
    >
      {sortedNonEmptyWorkspaces.map(workspace => (
        <Accordion
          key={workspace.id}
          defaultExpanded
          elevation={4}
          sx={{ '&:before': { display: 'none' } }}
        >
          <AccordionSummary
            expandIcon={
              <SvgIcon fontSize="large">
                <IconCustomMenuDown />
              </SvgIcon>
            }
          >
            <Typography variant="h6">{workspace.name}</Typography>
          </AccordionSummary>
          <AccordionDetails>
            {R.sortBy(
              queuesByWorkspace[workspace.url] || [],
              queue => queue.name
            ).map(queue => {
              return (
                <LinkWithNameAndId
                  link={`/queues/${queue.id}/settings/fields/${rowForQueuesDialog.fieldId}`}
                  name={queue.name}
                  id={String(queue.id)}
                  key={queue.id}
                />
              );
            })}
          </AccordionDetails>
        </Accordion>
      ))}
    </Dialog>
  );
};
